import Vue from "vue";
import i18n from "@/i18n";

// How many times to retry a request that was rate limited (429).
const MAX_RETRIES = 3;
// Backoff used when the server doesn't send a Retry-After header.
const BASE_DELAY_MS = 1000;
const MAX_DELAY_MS = 10000;

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

// Retry-After can be a number of seconds or an HTTP date.
function retryAfterMs(response) {
  const header = response?.headers?.["retry-after"];
  if (!header) {
    return null;
  }

  const seconds = Number(header);
  if (!Number.isNaN(seconds)) {
    return seconds * 1000;
  }

  const date = Date.parse(header);
  return Number.isNaN(date) ? null : Math.max(0, date - Date.now());
}

function backoffMs(attempt) {
  const exp = BASE_DELAY_MS * 2 ** attempt;
  const jitter = Math.random() * BASE_DELAY_MS;
  return Math.min(exp + jitter, MAX_DELAY_MS);
}

function toastServerError() {
  Vue.toasted.show(i18n.t("errors.server"), {
    type: "error",
    theme: "bubble",
    position: "top-center",
    duration: 8000,
  });
}

// Retries rate-limited (429) requests with backoff, and shows the user an
// error when the API fails with a 5xx or stays rate limited after retrying.
// The error is still rejected so callers stop what they were doing.
export default function installApiRetry(axios, { onError } = {}) {
  axios.interceptors.response.use(undefined, async (error) => {
    const { config, response } = error;
    const status = response?.status;

    if (status === 429 && config) {
      config.__retryCount = config.__retryCount || 0;

      if (config.__retryCount < MAX_RETRIES) {
        const delay = Math.min(
          retryAfterMs(response) ?? backoffMs(config.__retryCount),
          MAX_DELAY_MS
        );
        config.__retryCount += 1;
        await sleep(delay);
        return axios(config);
      }
    }

    if (onError) {
      onError(error);
    }

    if (status === 429 || status >= 500) {
      toastServerError();
    }

    return Promise.reject(error);
  });
}
